import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { enums } from "../../constants/enum";
import usePurchaseService from "../../services/api/purchaseService";
import dateFormatter from "../../util/dateFormatter";

import { ArrowLongRightIcon } from "../../assets/icons";
import itemImage from "../../assets/images/inventory-item.png";
import { Badge, Button, Loading, PageTitle, Section } from "../../components";
import "./PurchasesPage.scss";
import currencyFormatter from "../../util/currencyFormatter";
import { useCallback } from "react";

function PurchaseDetailsPage() {
  const { purchaseId } = useParams();
  const navigate = useNavigate();
  const { queryKey, getPurchaseById } = usePurchaseService();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: [queryKey, purchaseId],
    queryFn: () => getPurchaseById(purchaseId),
    enabled: !!purchaseId,
  });

  const makeBadgeVariant = useCallback((itemCategory) => {
    switch (itemCategory) {
      case enums.ITEM_CATEGORIES.FRAME:
        return "info";
      case enums.ITEM_CATEGORIES.LENSE:
        return "success";
      case enums.ITEM_CATEGORIES.ACCESSORIES:
        return "warning";
      case enums.ITEM_CATEGORIES.SUNGLASS:
        return "danger";
      default:
        return "info";
    }
  }, []);

  if (isLoading) {
    return <Loading variant="fullScreen" />;
  }

  if (isError) {
    toast.error(
      error?.response?.data?.payload?.message || enums.DEFAULT_ERROR_MESSAGE,
    );
    navigate(-1);
  }

  return (
    <div className="page purchase-details-page">
      <PageTitle text="Purchase Details" prev="/purchases" />

      <Section>
        <div className="data-sec">
          <div className="left-bx">
            <div>
              <span>Purchase ID</span>
              <h2>{data?.purchase?._id}</h2>
            </div>

            <div>
              <span>Order Date & Time</span>
              <p>
                <small>Date: </small>
                {dateFormatter(data?.purchase?.createdAt)}
              </p>
            </div>

            <div>
              <span>Amount</span>
              <p>{currencyFormatter(data?.purchase?.totalPrice)} LKR</p>
            </div>
          </div>

          <div className="right-bx">
            <div>
              <span>Bill Number</span>
              <p>{data?.purchase?.billNumber}</p>
            </div>

            <div>
              <span>Last Updated At</span>
              <p>
                <small>Date: </small>
                {dateFormatter(data?.purchase?.updatedAt)}
              </p>
            </div>
          </div>
        </div>

        <div className="inventory-list">
          {data?.purchase?.items.map(({ item, quantity, sellingPrice }) => (
            <div className="item-card" key={item._id}>
              <div className="item-img">
                <div className="img-bx">
                  <img src={itemImage} alt="Item" />
                </div>
              </div>

              <div className="item-meta">
                <div className="item-name">
                  <i className="item-brand-letter">
                    {item?.itemName?.charAt(0) || "I"}
                  </i>
                  <p>
                    {item?.brand} {item?.itemName}
                  </p>
                </div>

                <div className="item-stats">
                  <div>
                    <Badge
                      className="badge"
                      text={item?.category}
                      variant={makeBadgeVariant(item?.category)}
                      outlined
                    />
                  </div>
                </div>
              </div>

              <div className="item-price">
                <span>Qty</span>
                <p>
                  {quantity} <small>x</small>
                </p>
              </div>

              <div className="item-price">
                <span>Sold Price</span>
                <p>
                  {currencyFormatter(sellingPrice)} <small>LKR</small>
                </p>
              </div>

              <div className="item-price">
                <span>Total Price</span>
                <p>
                  {currencyFormatter(sellingPrice * quantity)}{" "}
                  <small>LKR</small>
                </p>
              </div>

              <div className="item-actions">
                <Link to={`/inventory/details/${item._id}`}>
                  <Button
                    text="View Item"
                    prefixIcon={<ArrowLongRightIcon />}
                  />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}

export default PurchaseDetailsPage;
